import React, { useState, useEffect, useRef, useCallback, useContext, createContext, useMemo } from 'react';
import { Link,  useNavigate } from 'react-router-dom';
import { MdOutlineMessage, MdStorefront } from 'react-icons/md';
import { FiRadio } from 'react-icons/fi';
import { FiTrash2, FiHeart, FiUserPlus, FiUserCheck, FiEye } from 'react-icons/fi';
import { AiOutlineArrowLeft, AiOutlinePicture, AiOutlineSearch } from 'react-icons/ai';
import { client, urlFor } from '../sanity/sanityClient';
import { v4 as uuidv4 } from 'uuid';
import apiClient from "../utils/apiClient";
import { getCachedPosts, setCachedPosts } from '../db';
import {
  fetchFeedPage,
  fetchPersonalizedFeedPage,
  toggleLike,
  checkIsLiked,
  toggleFollow,
  checkIsFollowing,
  getFollowersCount,
  getLikers,
  getFollowersList,
  recordView,
} from '../services/feed';
import { fetchUsersByIds } from '../services/users';
import { searchUser } from '../utils/APIRoutes';
import './UploadForm.css';

/**
 * Shared cache for follower counts, keyed by userId. Without this,
 * every PostCard fetches getFollowersCount() independently — if the
 * same author has 5 posts on screen, that's 5 identical queries for
 * the same number. This context lives at the UploadForm level (see
 * the Provider in UploadForm's render) so every PostCard on the page
 * shares one cache: the first card for a given author fetches and
 * caches the count, every other card for that same author reads the
 * cached value instead of re-fetching. A follow/unfollow click also
 * updates the shared value, so ALL of that author's visible cards
 * reflect the new count immediately, not just the one that was clicked.
 */
const FollowersCountContext = createContext(null);

// Mobile detection hook
const useMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

/**
 * Lets any visitor search for a user by username, see their avatar,
 * follow/unfollow them right from the results, and click through to
 * discover their posts (reuses the existing isTrader/traderProducts
 * flow — clicking a result calls fetchTraderProducts(userId), same
 * as clicking a post-card avatar does).
 */
const UserSearch = ({ currentUser, onSelectUser }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [followingMap, setFollowingMap] = useState({}); // userId -> boolean
  const [followBusyId, setFollowBusyId] = useState(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  const closeSearch = () => {
    setOpen(false);
    setQuery('');
    setResults([]);
    setError('');
  };

  // Focus the input the moment the panel opens.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  // Close on an outside click, same expectation as any search popover.
  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        closeSearch();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setError('');
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(searchUser, {
          params: {
            q: trimmed,
            limit: 8,
            ...(currentUser?._id && { excludeUserId: currentUser._id }),
          },
        });
        setResults(data.users || []);
        setError('');
      } catch (err) {
        console.error('User search failed:', err);
        setError('Search failed. Please try again.');
        setResults([]);
      } finally {
        setLoading(false);
     }
   }, 350); // debounce so we're not hitting the backend on every keystroke

    return () => clearTimeout(debounceRef.current);
  }, [query, currentUser]);

  // Check follow status for the current result set once, whenever the
  // results change (not on every render).
  useEffect(() => {
    if (!currentUser || results.length === 0) return;
    let cancelled = false;

    Promise.all(
      results.map((u) =>
        checkIsFollowing(currentUser._id, u._id)
          .then((isFollowing) => [u._id, isFollowing])
          .catch(() => [u._id, false])
      )
    ).then((pairs) => {
      if (cancelled) return;
      setFollowingMap((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, currentUser?._id]);

  const handleFollowClick = async (e, targetUserId) => {
    e.stopPropagation();
    if (!currentUser || followBusyId) return;

    setFollowBusyId(targetUserId);
    const optimistic = !followingMap[targetUserId];
    setFollowingMap((prev) => ({ ...prev, [targetUserId]: optimistic }));

    try {
      const result = await toggleFollow(currentUser._id, targetUserId);
      setFollowingMap((prev) => ({ ...prev, [targetUserId]: result.following }));
    } catch (err) {
      console.error('Follow toggle failed:', err);
      setFollowingMap((prev) => ({ ...prev, [targetUserId]: !optimistic }));
    } finally {
      setFollowBusyId(null);
    }
  };

  return (
    <div className="user-search-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className="user-search-toggle-button"
        onClick={() => (open ? closeSearch() : setOpen(true))}
        title="Search users"
        aria-label="Search users"
      >
        <AiOutlineSearch />
      </button>

      {open && (
        <div className="user-search-panel">
          <div className="user-search-input-group">
            <AiOutlineSearch className="user-search-icon" />
            <input
              ref={inputRef}
              type="text"
              className="user-search-input"
              placeholder="Search for a user..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {query.trim() && (
            <div className="user-search-results">
              {loading ? (
                <p className="user-search-status">Searching...</p>
              ) : error ? (
                <p className="user-search-status error">{error}</p>
              ) : results.length === 0 ? (
                <p className="user-search-status">No users found.</p>
              ) : (
                results.map((u) => (
                  <div
                    key={u._id}
                    className="user-search-result"
                    onClick={() => {
                      onSelectUser(u._id);
                      closeSearch();
                    }}
                  >
                    {u.avatarImage ? (
                      <img
                        src={u.avatarImage.startsWith('http') ? u.avatarImage : `data:image/svg+xml;base64,${u.avatarImage}`}
                        alt={u.username}
                        className="user-search-avatar"
                      />
                    ) : (
                      <div className="user-search-avatar placeholder-avatar">
                        {u.username?.charAt(0)?.toUpperCase()}
                      </div>
                    )}

                    <span className="user-search-name">{u.username}</span>

                    {currentUser && (
                      <button
                        type="button"
                        className={`follow-button user-search-follow-button ${followingMap[u._id] ? 'following' : ''}`}
                        disabled={followBusyId === u._id}
                        onClick={(e) => handleFollowClick(e, u._id)}
                      >
                        {followingMap[u._id] ? (
                          <>
                            <FiUserCheck /> Following
                          </>
                        ) : (
                          <>
                            <FiUserPlus /> Follow
                          </>
                        )}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Shows who liked a post, or who follows a user — same shape either
 * way: a list of avatar + name + a follow/follow-back button, sourced
 * by resolving Mongo user ids (from getLikers/getFollowersList in
 * feed.js) through the backend's batch endpoint. `fetchIds` is called
 * once on mount; the modal itself doesn't care whether it's showing
 * likers or followers, only the caller's fetchIds/title differ.
 */
const UserListModal = ({ title, fetchIds, currentUser, onClose, onSelectUser, followButtonLabel = 'Follow' }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [followingMap, setFollowingMap] = useState({});
  const [followBusyId, setFollowBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchIds()
      .then((ids) => fetchUsersByIds(ids))
      .then((list) => {
        if (!cancelled) {
          setUsers(list);
          setError('');
        }
      })
      .catch((err) => {
        console.error('Failed to load user list:', err);
        if (!cancelled) setError('Failed to load. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Runs once per mount — this modal is only ever mounted while
    // visible (conditionally rendered by the parent), so there's no
    // stale-fetchIds concern from an unchanging dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentUser || users.length === 0) return;
    let cancelled = false;

    Promise.all(
      users.map((u) =>
        checkIsFollowing(currentUser._id, u._id)
          .then((isFollowing) => [u._id, isFollowing])
          .catch(() => [u._id, false])
      )
    ).then((pairs) => {
      if (!cancelled) setFollowingMap((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, currentUser?._id]);

  const handleFollowClick = async (e, targetUserId) => {
    e.stopPropagation();
    if (!currentUser || followBusyId) return;

    setFollowBusyId(targetUserId);
    const optimistic = !followingMap[targetUserId];
    setFollowingMap((prev) => ({ ...prev, [targetUserId]: optimistic }));

    try {
      const result = await toggleFollow(currentUser._id, targetUserId);
      setFollowingMap((prev) => ({ ...prev, [targetUserId]: result.following }));
    } catch (err) {
      console.error('Follow toggle failed:', err);
      setFollowingMap((prev) => ({ ...prev, [targetUserId]: !optimistic }));
    } finally {
      setFollowBusyId(null);
    }
  };

  return (
    <div className="user-list-modal-backdrop" onClick={onClose}>
      <div className="user-list-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-list-modal-header">
          <h4>{title}</h4>
          <button type="button" className="close-form-button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="user-list-modal-body">
          {loading ? (
            <p className="user-search-status">Loading...</p>
          ) : error ? (
            <p className="user-search-status error">{error}</p>
          ) : users.length === 0 ? (
            <p className="user-search-status">No one yet.</p>
          ) : (
            users.map((u) => (
              <div
                key={u._id}
                className="user-search-result"
                onClick={() => {
                  if (onSelectUser) onSelectUser(u._id);
                }}
              >
                {u.avatarImage ? (
                  <img
                    src={u.avatarImage.startsWith('http') ? u.avatarImage : `data:image/svg+xml;base64,${u.avatarImage}`}
                    alt={u.username}
                    className="user-search-avatar"
                  />
                ) : (
                  <div className="user-search-avatar placeholder-avatar">
                    {u.username?.charAt(0)?.toUpperCase()}
                  </div>
                )}

                <span className="user-search-name">{u.username}</span>

                {currentUser && currentUser._id !== u._id && (
                  <button
                    type="button"
                    className={`follow-button user-search-follow-button ${followingMap[u._id] ? 'following' : ''}`}
                    disabled={followBusyId === u._id}
                    onClick={(e) => handleFollowClick(e, u._id)}
                  >
                    {followingMap[u._id] ? (
                      <>
                        <FiUserCheck /> Following
                      </>
                    ) : (
                      <>
                        <FiUserPlus /> {followButtonLabel}
                      </>
                    )}
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const UploadForm = ({ currentUser, isTrader, traderProducts, fetchTraderProducts }) => {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const [displayedPosts, setDisplayedPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [liveSessionActive, setLiveSessionActive] = useState(false);
  const [formErrors, setFormErrors] = useState({ title: '', images: '', general: '' });

  const observerRef = useRef();
  // Guards against the intersection observer firing a second fetch
  // while the first is still in flight.
  const fetchInFlight = useRef(false);

  // ---- Followers-count cache, shared by every PostCard on this page
  // via FollowersCountContext (see the Provider below in the return
  // statement, and the definition/rationale above the context itself).

  const [followersCounts, setFollowersCounts] = useState({}); // userId -> count, drives rendering
  // Mirrors followersCounts but as a ref, so ensureFollowersCount can
  // check "already cached?" without depending on the state value —
  // keeping its function identity stable across renders. Without this,
  // ensureFollowersCount would get a new identity every time ANY
  // author's count changed, which would re-trigger every PostCard's
  // effect on every unrelated update (harmless — cache-checked — but
  // needless churn across a feed with many cards).
  const followersCountsRef = useRef({});
  // Tracks in-flight fetches per userId so that if two PostCards for
  // the same author mount in the same tick (before either fetch has
  // resolved and populated the cache), the second one reuses the
  // first's in-flight promise instead of firing a duplicate query.
  const followersFetchesRef = useRef({}); // userId -> Promise<number>

// Add this effect alongside your other mount-time effects — a
// lightweight existence check, not a full session fetch (Live.js
// does the real fetch once the user actually navigates there):
useEffect(() => {
  let cancelled = false;
  const checkLive = async () => {
    try {
      const count = await client.fetch(
        `count(*[_type == "liveSession" && isLive == true])`
      );
      if (!cancelled) setLiveSessionActive(count > 0);
    } catch (err) {
      console.error('Live session check failed:', err);
    }
  };
  checkLive();
  // Poll every 60s so the badge appears/disappears without a full
  // page reload as sessions start and end — cheap, since it's just a
  // count() query, not fetching the session document itself.
  const interval = setInterval(checkLive, 60000);
  return () => {
    cancelled = true;
    clearInterval(interval);
  };
}, []);

  const ensureFollowersCount = useCallback((userId) => {
    if (!userId) return;
    if (followersCountsRef.current[userId] !== undefined) return; // already cached
    if (followersFetchesRef.current[userId]) return followersFetchesRef.current[userId]; // already in flight

    const fetchPromise = getFollowersCount(userId)
      .then((count) => {
        followersCountsRef.current = { ...followersCountsRef.current, [userId]: count };
        setFollowersCounts(followersCountsRef.current);
        return count;
      })
      .catch((err) => {
        console.error('Followers count fetch failed:', err);
      })
      .finally(() => {
        delete followersFetchesRef.current[userId];
      });

    followersFetchesRef.current[userId] = fetchPromise;
    return fetchPromise;
  }, []);

  const adjustFollowersCount = useCallback((userId, delta) => {
    if (!userId) return;
    if (followersCountsRef.current[userId] === undefined) return; // nothing cached yet — next fetch will get the real number
    followersCountsRef.current = {
      ...followersCountsRef.current,
      [userId]: Math.max(0, followersCountsRef.current[userId] + delta),
    };
    setFollowersCounts(followersCountsRef.current);
  }, []);

  // Memoized so the context value only changes identity when the
  // counts themselves change — without this, every PostCard would
  // re-render on every UploadForm render (typing in the post form,
  // pagination, etc.), not just when a followers count actually updates.
  const followersCountCacheValue = useMemo(
    () => ({ counts: followersCounts, ensureFollowersCount, adjustFollowersCount }),
    [followersCounts, ensureFollowersCount, adjustFollowersCount]
  );

  // ---- Feed loading (ranked + cursor-paginated, not shuffle-all) ----

const loadFirstPage = useCallback(async () => {
  setInitialLoading(true);
  fetchInFlight.current = true;

  // Instant paint from IndexedDB — this is the piece that was
  // missing. Without it, opening the feed offline (or on a slow
  // connection) always showed the loading spinner with nothing behind
  // it, unlike contacts/categories/messages which already fall back
  // to cached data immediately.
  try {
    const cached = await getCachedPosts();
    if (cached.length) setDisplayedPosts(cached);
  } catch (cacheErr) {
    // Isolated from the network fetch below — same principle as the
    // fetchCategories fix: a cache-read failure must never block the
    // network attempt that follows.
    console.error('Post cache read failed (continuing to network fetch):', cacheErr);
  }

  try {
    const result = currentUser
      ? await fetchPersonalizedFeedPage(currentUser._id)
      : await fetchFeedPage();

    setDisplayedPosts(result.posts);
    setCursor(result.nextCursor ?? null);
    setHasMore(result.hasMore);

    // Write-through so the next offline/cold open has this page ready.
    try {
      await setCachedPosts(result.posts);
    } catch (cacheErr) {
      console.error('Post cache write failed (non-fatal):', cacheErr);
    }
  } catch (error) {
    console.error('Error fetching posts (likely offline):', error?.message || error);
    // Deliberately no setDisplayedPosts([]) or setFormErrors(...) here
    // on a pure fetch failure — whatever loaded from cache above (or
    // was already on screen from a prior successful load) stays
    // visible instead of being replaced by an empty/error state.
    // Only surface a user-facing error if there was NOTHING to show
    // at all, cache included:
    setDisplayedPosts((prev) => {
      if (prev.length === 0) {
        setFormErrors((fe) => ({ ...fe, general: 'Failed to load posts' }));
      }
      return prev;
    });
  } finally {
    setInitialLoading(false);
    fetchInFlight.current = false;
  }
}, [currentUser]);

const loadNextPage = useCallback(async () => {
  if (fetchInFlight.current || !hasMore) return;
  fetchInFlight.current = true;
  setLoadingMore(true);
  try {
    // Personalized feeds don't currently paginate past page 1 (see
    // note in feed.js) — once a user follows people, extend
    // fetchPersonalizedFeedPage with the same cursor pattern below.
    const result = await fetchFeedPage({ cursor });
    setDisplayedPosts((prev) => [...prev, ...result.posts]);
    setCursor(result.nextCursor ?? null);
    setHasMore(result.hasMore);

    // Cache the appended page too, so scrolling further down and then
    // going offline still has that content on the next open — same
    // treatment as fetchCategories' isLoadMore branch.
    try {
      await setCachedPosts(result.posts);
    } catch (cacheErr) {
      console.error('Post cache write failed (non-fatal):', cacheErr);
    }
  } catch (error) {
    console.error('Error fetching next page (likely offline):', error?.message || error);
    // No hasMore(false) here on a network error — an offline load-more
    // attempt shouldn't permanently disable infinite scroll for the
    // rest of the session once connectivity returns.
  } finally {
    setLoadingMore(false);
    fetchInFlight.current = false;
  }
}, [cursor, hasMore]);

  useEffect(() => {
    if (!isTrader) loadFirstPage();
  }, [isTrader, loadFirstPage]);

  // Set up intersection observer for infinite scroll
  const lastPostElementRef = useCallback(
    (node) => {
      if (loadingMore) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loadingMore, hasMore, loadNextPage]
  );
  
  const goToLive=()=>{
    navigate('/golive')
  }
  // ---- Post creation ----

  const handleImageChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length === 0) {
      setFormErrors((prev) => ({ ...prev, images: 'Please select at least one image' }));
      return;
    }
    setImages(selectedFiles);
    setFormErrors((prev) => ({ ...prev, images: '' }));
  };

  const validateForm = () => {
    const errors = { title: '', images: '', general: '' };
    if (!title.trim()) errors.title = 'Title is required';
    if (images.length === 0) errors.images = 'At least one image is required';
    setFormErrors(errors);
    return !errors.title && !errors.images;
  };

  const generateSlug = async (name) => {
    let baseSlug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');

    try {
      const count = await client.fetch(
        `count(*[_type == "posts" && slug.current == $slug])`,
        { slug: baseSlug }
      );
      return count > 0 ? `${baseSlug}-${count + 1}` : baseSlug;
    } catch (error) {
      console.error('Error checking slug uniqueness:', error);
      return `${baseSlug}-${Math.random().toString(36).substring(2, 6)}`;
    }
  };

  const resetForm = () => {
    setTitle('');
    setImages([]);
    setFormErrors({ title: '', images: '', general: '' });
    setShowForm(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setFormErrors((prev) => ({ ...prev, general: '' }));

    try {
      const imageUploads = await Promise.all(
        images.map((image) => client.assets.upload('image', image))
      );
      const slugValue = await generateSlug(title);

      const doc = {
        _type: 'posts',
        // Mongo user _id as a plain string — NOT a Sanity reference.
        // Users live in MongoDB, not as Sanity documents, so a
        // `reference` here fails every write with "references
        // non-existent document".
        user: currentUser._id,
        name: currentUser.username,
        avatar: currentUser.avatarImage,
        title,
        slug: { _type: 'slug', current: slugValue },
        image: imageUploads.map((upload) => ({
          _type: 'image',
          _key: uuidv4(),
          asset: { _ref: upload._id, _type: 'reference' },
        })),
        likesCount: 0,
        commentsCount: 0,
        viewsCount: 0,
        sharesCount: 0,
        score: 0, // picked up by the next scheduled recalculation
      };

      const created = await client.create(doc);

      resetForm();

      // Prepend the new post locally instead of re-fetching the whole
      // feed — we already have everything we need to render it.
      setDisplayedPosts((prev) => [
        {
          ...doc,
          _id: created._id,
          _createdAt: new Date().toISOString(),
          userId: currentUser._id,
        },
        ...prev,
      ]);
    } catch (error) {
      console.error('Upload error:', error);
      setFormErrors((prev) => ({ ...prev, general: 'Upload failed. Please try again.' }));
    } finally {
      setLoading(false);
    }
  };

  const handlePostDeleted = (id) => {
    setDisplayedPosts((prev) => prev.filter((p) => p._id !== id));
  };

  const postsToRender = isTrader ? traderProducts : displayedPosts;

  return (
    <FollowersCountContext.Provider value={followersCountCacheValue}>
    <div className={`upload-form ${isMobile ? 'mobile' : 'desktop'}`}>
      <div className="top-bar">
        <div className="top-bar-main">
          <div className="live-tabs" style={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}>
          {currentUser && !isTrader && (
              <button
                onClick={goToLive}
                // navigate must come from useNavigate() — add
                // `const navigate = useNavigate();` near your other hooks if
                // UploadForm doesn't already have it, and import it from
                // 'react-router-dom'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'white',
                  color: '#f97316',
                  border: '1px solid #f97316',
                  borderRadius: '20px',
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  marginLeft: '8px',
                }}
              >
                <FiRadio size={14} />
                Go Live
              </button>
            )}
            {liveSessionActive && (            
          <Link
            to="/live-discovery"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: '#f97316',
              color: 'white',
              textDecoration: 'none',
              padding: '6px 14px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 700,
            }}
          >
            <FiRadio size={14} />
            LIVE NOW
          </Link>
          )}
     </div>
          {currentUser ? (
            isTrader ? (
              ''
            ) : (
              <div className="post-form-wrapper">
                {!showForm ? (
                  <button className="open-post-button" onClick={() => setShowForm(true)}>
                    + Create Post
                  </button>
            ) : (
              <form onSubmit={handleSubmit} className="post-form-container">
                <div className="form-header">
                  <div className="form-user-info">
                    {currentUser?.avatarImage ? (
                      <img
                        src={
                          currentUser.avatarImage.startsWith('http')
                            ? currentUser.avatarImage
                            : `data:image/svg+xml;base64,${currentUser.avatarImage}`
                        }
                        alt={currentUser.username}
                        className="form-user-avatar"
                      />
                    ) : (
                      <div className="form-user-avatar placeholder-avatar">
                        {currentUser?.username?.charAt(0)}
                      </div>
                    )}
                    <div className="form-user-details">
                      <h4>{currentUser?.username}</h4>
                      <small>Share your business, products or ideas</small>
                    </div>
                  </div>

                  <button type="button" className="close-form-button" onClick={() => setShowForm(false)}>
                    ✕
                  </button>
                </div>

                <div className="post-input-wrapper">
                  <textarea
                    placeholder={`What's on your mind, ${currentUser?.username}?`}
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      setFormErrors((prev) => ({ ...prev, title: '' }));
                    }}
                    className={`post-textarea ${formErrors.title ? 'error' : ''}`}
                    required
                  />

                  <input
                    id="post-image-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />

                  <label htmlFor="post-image-upload" className="media-upload-button">
                    <AiOutlinePicture />
                  </label>
                </div>

                {formErrors.title && <span className="error-text">{formErrors.title}</span>}
                {formErrors.images && <span className="error-text">{formErrors.images}</span>}
                {formErrors.general && <p className="error-text">{formErrors.general}</p>}

                {images.length > 0 && (
                  <div className="selected-images-info">
                    {images.length} image{images.length > 1 ? 's' : ''} selected
                  </div>
                )}

                <div className="image-preview">
                  {images.map((image, index) => (
                    <div key={index} className="preview-image-wrapper">
                      <img src={URL.createObjectURL(image)} alt="Preview" className="preview-image" />
                    </div>
                  ))}
                </div>

                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Uploading...' : 'Post Your Brand'}
                </button>
              </form>
            )}
          </div>
            )
          ) : (
            <h4>
              <Link to="/register" className="register-link">
                Register
              </Link>{' '}
              now to start posting your brands
            </h4>
          )}
        </div>

        <UserSearch currentUser={currentUser} onSelectUser={(userId) => fetchTraderProducts(userId)} />
      </div>

      <div className={`posts-list ${isMobile ? 'mobile-grid' : 'desktop-grid'}`}>
        {!isTrader ? (
          initialLoading ? (
            <div style={{ textAlign: 'center' }}>
              <p>Loading posts...</p>
            </div>
          ) : postsToRender.length > 0 ? (
            postsToRender.map((post, index) => {
              const isLast = index === postsToRender.length - 1;
              return (
                <PostCard
                  ref={isLast ? lastPostElementRef : null}
                  key={post._id}
                  post={post}
                  currentUser={currentUser}
                  fetchTraderProducts={fetchTraderProducts}
                  isTrader={isTrader}
                  isMobile={isMobile}
                  onDelete={handlePostDeleted}
                />
              );
            })
          ) : (
            <div style={{ textAlign: 'center' }}>
              <p>No posts yet.</p>
            </div>
          )
        ) : traderProducts.length > 0 ? (
          traderProducts.map((post) => (
            <PostCard
              key={post._id}
              post={post}
              currentUser={currentUser}
              fetchTraderProducts={fetchTraderProducts}
              isTrader={isTrader}
              isMobile={isMobile}
              onDelete={handlePostDeleted}
            />
          ))
        ) : (
          <p className="back-to-posts" onClick={() => fetchTraderProducts()}>
            <AiOutlineArrowLeft style={{ fontSize: '20px' }} />
            The user has no listed products!
          </p>
        )}

        {!isTrader && loadingMore && hasMore && (
          <div className="loading-more">
            <div className="loading-spinner"></div>
            <p>Loading more posts...</p>
          </div>
        )}

        {!isTrader && !hasMore && displayedPosts.length > 0 && (
          <div className="no-more-posts">
            <p>You've reached the end! No more posts to load.</p>
          </div>
        )}
      </div>
    </div>
    </FollowersCountContext.Provider>
  );
};

const PostCard = React.memo(
  React.forwardRef(({ post, fetchTraderProducts, currentUser, isTrader, isMobile, onDelete }, ref) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const [liked, setLiked] = useState(false);
    const [likesCount, setLikesCount] = useState(post.likesCount || 0);
    const [likeBusy, setLikeBusy] = useState(false);
    // useState-driven likeBusy updates across a render, which isn't
    // fast enough to block two clicks fired within the same tick (a
    // fast double-click, or a mobile "ghost click"). This ref updates
    // synchronously the instant the handler runs, so a second call
    // sees it immediately — likeBusy state still drives the UI
    // (disabled styling), the ref is purely the re-entrancy guard.
    const likeBusyRef = useRef(false);

    const [viewsCount, setViewsCount] = useState(post.viewsCount || 0);

    const [following, setFollowing] = useState(false);
    const [followBusy, setFollowBusy] = useState(false);
    const [followChecked, setFollowChecked] = useState(false);

    // Shared cache — see FollowersCountContext definition for why this
    // exists instead of a local useState + per-card fetch.
    const followersCache = useContext(FollowersCountContext);

    const [showLikers, setShowLikers] = useState(false);
    const [showFollowersList, setShowFollowersList] = useState(false);

    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    const hasRecordedView = useRef(false);
    // Ref guard (not state) for the follow click handler: state updates
    // are batched and only visible after a re-render, so a second click
    // fired in the same tick (fast double-click, or a touch event
    // followed by a synthetic click) would still see the old state
    // value and slip through. This ref updates synchronously, so the
    // second call is blocked immediately — same pattern as likeBusyRef
    // above. followBusy (state) still drives the disabled styling.
    const followBusyRef = useRef(false);

    // Post author id: the projection aliases the plain-string Mongo
    // user id to `userId`. Fall back to a raw `user` field only for
    // any post object that might reach this component from an older
    // code path that hasn't been updated to the new projection.
    const postOwnerId = post.userId || post.user;
    const isOwnPost = currentUser?._id === postOwnerId;
    const canFollow = !!currentUser && !!postOwnerId && !isOwnPost;

    // Record a view once, the first time the card becomes visible.
    useEffect(() => {
      if (hasRecordedView.current) return;
      const node = typeof ref === 'function' ? null : ref?.current;
      const target = node || document.getElementById(`post-${post._id}`);
      if (!target) return;

      const viewObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !hasRecordedView.current) {
            hasRecordedView.current = true;
            recordView(post._id)
              .then(() => setViewsCount((prev) => prev + 1))
              .catch((err) => console.error('View tracking failed:', err));
            viewObserver.disconnect();
          }
        },
        { threshold: 0.5 }
      );
      viewObserver.observe(target);
      return () => viewObserver.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post._id]);

    // Check like status for this post/user pair.
    useEffect(() => {
      if (!currentUser) return;
      let cancelled = false;
      checkIsLiked(post._id, currentUser._id)
        .then((isLiked) => {
          if (!cancelled) setLiked(isLiked);
        })
        .catch((err) => console.error('Like status check failed:', err));
      return () => {
        cancelled = true;
      };
    }, [post._id, currentUser]);

    // Check follow status for this post's author.
    useEffect(() => {
      if (!canFollow) {
        setFollowChecked(true);
        return;
      }
      let cancelled = false;
      checkIsFollowing(currentUser._id, postOwnerId)
        .then((result) => {
          if (!cancelled) {
            setFollowing(result);
            setFollowChecked(true);
          }
        })
        .catch((err) => {
          console.error('Follow status check failed:', err);
          if (!cancelled) setFollowChecked(true);
        });
      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postOwnerId, currentUser?._id, canFollow]);

    // Total follower count for this post's author — shown to everyone,
    // not gated behind login, since it's public info about the author
    // rather than something tied to the viewer's own follow state.
    // Sourced from the shared FollowersCountContext cache: this only
    // fires an actual query the first time a given author is seen on
    // the page, every other card for that same author reads the
    // cached number instead of re-fetching it.
    const followersCount = followersCache?.counts[postOwnerId] ?? null;

    useEffect(() => {
      if (!postOwnerId || !followersCache) return;
      followersCache.ensureFollowersCount(postOwnerId);
    }, [postOwnerId, followersCache]);

    const handleSwipe = () => {
      const distance = touchStartX.current - touchEndX.current;
      if (distance > 50) {
        setCurrentImageIndex((prev) => (prev === post.image.length - 1 ? 0 : prev + 1));
      }
      if (distance < -50) {
        setCurrentImageIndex((prev) => (prev === 0 ? post.image.length - 1 : prev - 1));
      }
    };

    const handleDelete = async (e) => {
      e.stopPropagation();
      const confirmed = window.confirm('Delete this post permanently?');
      if (!confirmed) return;

      try {
        await client.delete(post._id);
        onDelete(post._id);
      } catch (err) {
        console.error(err);
        alert('Failed to delete post.');
      }
    };

    const handleLikeClick = async (e) => {
      e.stopPropagation();
      if (!currentUser || likeBusyRef.current) return;

      likeBusyRef.current = true;
      setLikeBusy(true);
      // Optimistic update — reconcile with the server response after.
      const optimisticLiked = !liked;
      setLiked(optimisticLiked);
      setLikesCount((prev) => prev + (optimisticLiked ? 1 : -1));

      try {
        const result = await toggleLike(post._id, currentUser._id);
        setLiked(result.liked);
      } catch (err) {
        console.error('Like toggle failed:', err);
        // Revert on failure
        setLiked(!optimisticLiked);
        setLikesCount((prev) => prev + (optimisticLiked ? -1 : 1));
      } finally {
        likeBusyRef.current = false;
        setLikeBusy(false);
      }
    };

    const handleFollowClick = async (e) => {
      e.stopPropagation();
      if (!canFollow || followBusyRef.current) return;

      followBusyRef.current = true;
      setFollowBusy(true);
      // Optimistic update — reconcile with the server response after.
      // adjustFollowersCount updates the SHARED cache, so every other
      // card for this same author (if any are on screen) reflects the
      // new count too, not just this one.
      const optimisticFollowing = !following;
      setFollowing(optimisticFollowing);
      followersCache?.adjustFollowersCount(postOwnerId, optimisticFollowing ? 1 : -1);

      try {
        const result = await toggleFollow(currentUser._id, postOwnerId);
        setFollowing(result.following);
        if (result.following !== optimisticFollowing) {
          // Server disagreed with our guess (rare) — correct the count too.
          followersCache?.adjustFollowersCount(postOwnerId, result.following ? 1 : -1);
        }
      } catch (err) {
        console.error('Follow toggle failed:', err);
        setFollowing(!optimisticFollowing);
        followersCache?.adjustFollowersCount(postOwnerId, optimisticFollowing ? -1 : 1);
      } finally {
        followBusyRef.current = false;
        setFollowBusy(false);
      }
    };

    return (
      <div id={`post-${post._id}`} ref={ref} className={`post-card ${isMobile ? 'mobile-card' : ''}`}>
        <div className="user-credentials" onClick={() => fetchTraderProducts(postOwnerId)}>
          {isTrader ? (
            <div className="user-avatar-column">
              <div className="back-arrow-avatar">
                <AiOutlineArrowLeft style={{ fontSize: '40px' }} />
              </div>
              {followersCount !== null && (
                <span
                  className="followers-count-label"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFollowersList(true);
                  }}
                >
                  {followersCount} followers
                </span>
              )}
            </div>
          ) : (
            <div className="user-avatar-column">
              {postOwnerId && post.avatar ? (
                <img
                  src={
                    post.avatar.startsWith('http')
                      ? post.avatar
                      : `data:image/svg+xml;base64,${post.avatar}`
                  }
                  alt={`${post.name}'s avatar`}
                  className="avatar-image"
                  onError={(e) => {
                    e.target.src =
                      'https://ui-avatars.com/api/?name=' + encodeURIComponent(post.name || 'User');
                  }}
                />
              ) : (
                <p className="user-name"></p>
              )}
              {followersCount !== null && (
                <span
                  className="followers-count-label"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFollowersList(true);
                  }}
                >
                  {followersCount} followers
                </span>
              )}
            </div>
          )}
          {post.name && <p className="user-name">{post.name}</p>}

          {/* Follow / Unfollow — only rendered for signed-in users
              looking at someone else's post, never the post's owner. */}
          {canFollow && followChecked && (
            <button
              type="button"
              className={`follow-button ${following ? 'following' : ''}`}
              onClick={handleFollowClick}
              disabled={followBusy}
              title={following ? 'Unfollow' : 'Follow'}
            >
              {following ? <FiUserCheck /> : <FiUserPlus />}
              <span>{following ? '✓' : ''}</span>
            </button>
          )}

          <div className="link-tags">
            {isOwnPost && (
              <FiTrash2 className="delete-post-icon" onClick={handleDelete} title="Delete post" />
            )}

            <FiHeart
              className={`like-post-icon ${liked ? 'liked' : ''}`}
              onClick={handleLikeClick}
              title={liked ? 'Unlike' : 'Like'}
              style={{
                fill: liked ? 'currentColor' : 'none',
                cursor: likeBusy ? 'default' : 'pointer',
                opacity: likeBusy ? 0.6 : 1,
                margin: '4px',
              }}
            />

            <Link style={{ margin: '4px', textDecoration: 'none', color: '#111' }} to={`/chat/${postOwnerId}`}>
              <MdOutlineMessage />
            </Link>

            <Link style={{ textDecoration: 'none', color: '#111' }} to={`/shop/${postOwnerId}`}>
              <MdStorefront />
            </Link>
          </div>
        </div>

        <div className="post-images">
          {post.image && post.image.length > 0 && (
            <div
              className="image-slider"
              onTouchStart={(e) => {
                touchStartX.current = e.targetTouches[0].clientX;
              }}
              onTouchMove={(e) => {
                touchEndX.current = e.targetTouches[0].clientX;
              }}
              onTouchEnd={handleSwipe}
            >
              <img src={urlFor(post.image[currentImageIndex])} alt={`Uploaded by ${post.name}`} className="post-image" />

              {post.image.length > 1 && (
                <>
                  <div className="image-count">
                    {currentImageIndex + 1}/{post.image.length}
                  </div>
                  <div className="slider-dots">
                    {post.image.map((_, index) => (
                      <span key={index} className={`dot ${currentImageIndex === index ? 'active-dot' : ''}`} />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="post-meta">
          <span className="likes-count" onClick={() => setShowLikers(true)}>
            {likesCount} likes
          </span>
          <span className="views-count">
            <FiEye /> {viewsCount}
          </span>
        </div>

        <h5>{post.title}</h5>

        {showLikers && (
          <UserListModal
            title="Liked by"
            fetchIds={() => getLikers(post._id)}
            currentUser={currentUser}
            followButtonLabel="Follow"
            onClose={() => setShowLikers(false)}
            onSelectUser={(userId) => {
              setShowLikers(false);
              fetchTraderProducts(userId);
            }}
          />
        )}

        {showFollowersList && (
          <UserListModal
            title={`${post.name || 'User'}'s followers`}
            fetchIds={() => getFollowersList(postOwnerId)}
            currentUser={currentUser}
            followButtonLabel="Follow Back"
            onClose={() => setShowFollowersList(false)}
            onSelectUser={(userId) => {
              setShowFollowersList(false);
              fetchTraderProducts(userId);
            }}
          />
        )}
      </div>
    );
  })
);

export default UploadForm;
